import React from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ListPlus, Plus, Search } from 'lucide-react';
import { IngredientForm, type IngredientSearchResultItem } from './IngredientForm';
import { IngredientList, Ingredient } from './IngredientList';
import { NutritionTotals } from './NutritionTotals';
import { cn } from '@/lib/utils';

interface IngredientsSectionProps {
  ingredients: Ingredient[];
  isAddingIngredient: boolean;
  ingredientSearchQuery: string;
  selectedIngredientName: string;
  selectedIngredientPreviewUrl?: string;
  selectedYieldFactor?: number | null;
  selectedRetentionFactor?: number | null;
  ingredientAmount: string;
  ingredientUnit: string;
  unitWeightGrams: string;
  unitWeightMl: string;
  effectiveAmountGrams: number | null;
  effectiveAmountMl: number | null;
  gramsPerMl: number | null;
  availableUnits: string[];
  unitLabelsByKey: Record<string, string>;
  unitWeightsByKey: Record<string, number>;
  nutritionData: Ingredient['nutritionData'] | null;
  isLoadingNutrition: boolean;
  isLoadingUnits: boolean;
  editingIngredientId: string | null;
  showIngredientResults: boolean;
  ingredientSearchResults: IngredientSearchResultItem[];
  isSearchingIngredients: boolean;
  bulkIngredientInput: string;
  isBulkAddingIngredients: boolean;
  totalMacros: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
    saturatedFat: number;
    alcohol: number;
  };
  servings: number;
  onStartAddIngredient: () => void;
  onSearchQueryChange: (value: string) => void;
  onIngredientSelect: (item: IngredientSearchResultItem) => void;
  onAmountChange: (value: string) => void;
  onUnitChange: (value: string) => void;
  onUnitWeightChange: (value: string) => void;
  onUnitWeightMlChange: (value: string) => void;
  onCloseDropdown: () => void;
  onSubmitIngredient: (options?: { continueAdding?: boolean }) => void;
  onCancelIngredient: () => void;
  onEditIngredient: (ingredient: Ingredient) => void;
  onRemoveIngredient: (id: string) => void;
  onBulkIngredientInputChange: (value: string) => void;
  onBulkAddIngredients: () => void;
}

type IngredientAddMode = 'single' | 'bulk';

export const IngredientsSection: React.FC<IngredientsSectionProps> = ({
  ingredients,
  isAddingIngredient,
  ingredientSearchQuery,
  selectedIngredientName,
  selectedIngredientPreviewUrl,
  selectedYieldFactor,
  selectedRetentionFactor,
  ingredientAmount,
  ingredientUnit,
  unitWeightGrams,
  unitWeightMl,
  effectiveAmountGrams,
  effectiveAmountMl,
  gramsPerMl,
  availableUnits,
  unitLabelsByKey,
  unitWeightsByKey,
  nutritionData,
  isLoadingNutrition,
  isLoadingUnits,
  editingIngredientId,
  showIngredientResults,
  ingredientSearchResults,
  isSearchingIngredients,
  bulkIngredientInput,
  isBulkAddingIngredients,
  totalMacros,
  servings,
  onStartAddIngredient,
  onSearchQueryChange,
  onIngredientSelect,
  onAmountChange,
  onUnitChange,
  onUnitWeightChange,
  onUnitWeightMlChange,
  onCloseDropdown,
  onSubmitIngredient,
  onCancelIngredient,
  onEditIngredient,
  onRemoveIngredient,
  onBulkIngredientInputChange,
  onBulkAddIngredients,
}) => {
  const [addMode, setAddMode] = React.useState<IngredientAddMode>('single');

  React.useEffect(() => {
    if (isAddingIngredient) {
      setAddMode('single');
    }
  }, [isAddingIngredient]);

  const handleSingleMode = () => {
    setAddMode('single');
    if (!isAddingIngredient) {
      onStartAddIngredient();
    }
  };

  const handleBulkMode = () => {
    setAddMode('bulk');
    if (isAddingIngredient) {
      onCancelIngredient();
    }
  };

  const modeButtonClass = (mode: IngredientAddMode) => cn(
    'inline-flex h-8 min-w-[92px] items-center justify-center gap-1.5 rounded-md px-3 text-xs font-semibold transition-colors',
    addMode === mode
      ? 'bg-[#2EB4E4] text-white shadow-sm'
      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
  );

  return (
    <div className="flex flex-col gap-4 lg:flex-row">
      {/* Left Column - Ingredients List */}
      <div className="flex-1 space-y-3">
        <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Add Ingredients</h3>
          <div className="inline-flex w-full rounded-lg border border-slate-200 bg-white p-1 shadow-sm sm:w-auto">
            <button
              type="button"
              className={cn(modeButtonClass('single'), 'flex-1 sm:flex-none')}
              onClick={handleSingleMode}
            >
              <Search className="h-3.5 w-3.5" />
              Single
            </button>
            <button
              type="button"
              className={cn(modeButtonClass('bulk'), 'flex-1 sm:flex-none')}
              onClick={handleBulkMode}
            >
              <ListPlus className="h-3.5 w-3.5" />
              Bulk
            </button>
          </div>
        </div>

        {addMode === 'single' && !isAddingIngredient ? (
          <Button
            type="button"
            size="sm"
            onClick={onStartAddIngredient}
            className="h-9 gap-1.5 border-0 bg-[#2EB4E4] px-3 text-xs text-white hover:bg-[#2EB4E4] hover:text-white"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Ingredient
          </Button>
        ) : null}

        {addMode === 'bulk' ? (
          <div className="rounded-lg border border-slate-100 bg-white p-3 shadow-sm">
            <Textarea
              value={bulkIngredientInput}
              onChange={(event) => onBulkIngredientInputChange(event.target.value)}
              placeholder="rice, paneer, tomato"
              rows={3}
              className="min-h-[84px] resize-y text-xs"
            />
            <div className="mt-2 flex justify-end">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={isBulkAddingIngredients || bulkIngredientInput.trim().length === 0}
                onClick={onBulkAddIngredients}
                className="h-9 shrink-0 border-[#2EB4E4] px-3 text-xs font-semibold text-[#1589b2] hover:bg-[#eefbff]"
              >
                {isBulkAddingIngredients ? 'Adding...' : 'Add List'}
              </Button>
            </div>
          </div>
        ) : null}
        <div className="space-y-6">
          {/* Add Ingredient Form */}
          {addMode === 'single' && isAddingIngredient && (
            <IngredientForm
              ingredientSearchQuery={ingredientSearchQuery}
              selectedIngredientName={selectedIngredientName}
              selectedIngredientPreviewUrl={selectedIngredientPreviewUrl}
              selectedYieldFactor={selectedYieldFactor}
              selectedRetentionFactor={selectedRetentionFactor}
              ingredientAmount={ingredientAmount}
              ingredientUnit={ingredientUnit}
              unitWeightGrams={unitWeightGrams}
              unitWeightMl={unitWeightMl}
              effectiveAmountGrams={effectiveAmountGrams}
              effectiveAmountMl={effectiveAmountMl}
              gramsPerMl={gramsPerMl}
              availableUnits={availableUnits}
              unitLabelsByKey={unitLabelsByKey}
              unitWeightsByKey={unitWeightsByKey}
              nutritionData={nutritionData}
              isLoadingNutrition={isLoadingNutrition}
              isLoadingUnits={isLoadingUnits}
              isEditing={Boolean(editingIngredientId)}
              showIngredientResults={showIngredientResults}
              ingredientSearchResults={ingredientSearchResults}
              isSearchingIngredients={isSearchingIngredients}
              onSearchQueryChange={onSearchQueryChange}
              onIngredientSelect={onIngredientSelect}
              onAmountChange={onAmountChange}
              onUnitChange={onUnitChange}
              onUnitWeightChange={onUnitWeightChange}
              onUnitWeightMlChange={onUnitWeightMlChange}
              onCloseDropdown={onCloseDropdown}
              onSubmit={onSubmitIngredient}
              onCancel={onCancelIngredient}
            />
          )}

          {/* Ingredients List */}
          <IngredientList
            ingredients={ingredients.filter(ing => ing.id !== editingIngredientId)}
            unitLabelsByKey={unitLabelsByKey}
            onEdit={onEditIngredient}
            onRemove={onRemoveIngredient}
          />
        </div>
      </div>

      {/* Right Column - Nutrition Totals */}
      <NutritionTotals totalMacros={totalMacros} servings={servings} />
    </div>
  );
};
